import apiClient from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  PendingExam,
  OngoingTraining,
  ClientDashboardStats,
  ClientDashboardData,
  ClientDashboardApiResponse,
  ClientDashboardPendingExam,
  ClientDashboardOngoingEducation,
  ClientDashboardUpcomingEducation,
} from "../types/client-dashboard.types";

const DASHBOARD_URL = "/api/client/dashboard";

function mapSummaryToStats(summary: ClientDashboardData["summary"]): ClientDashboardStats {
  const total = summary.completedEducations + summary.ongoingEducations;
  return {
    completedTrainings: { value: summary.completedEducations, total: total || 1 },
    ongoingTrainings: { value: summary.ongoingEducations, total: total || 1 },
    completedExams: { score: summary.completedExamsCount, total: summary.totalExamsCount },
    examSuccessRate: summary.examSuccessRatePercent,
  };
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:00`;
  return `${String(m).padStart(2, "0")}:00`;
}

function mapPendingExam(api: ClientDashboardPendingExam): PendingExam {
  const countdown =
    api.remainingDays == null
      ? i18n.t("clientDashboardCountdown.noExpiry")
      : api.remainingDays < 0
        ? i18n.t("clientDashboardCountdown.expired")
        : api.remainingDays === 0
          ? i18n.t("clientDashboardCountdown.today")
          : api.remainingDays === 1
            ? i18n.t("clientDashboardCountdown.oneDay")
            : i18n.t("clientDashboardCountdown.daysCount", { n: api.remainingDays });
  return {
    id: api.educationId,
    title: api.educationName,
    score: api.answeredCount,
    total: api.totalQuestionCount,
    timeSpent: "-",
    timeTotal: "-",
    countdown,
  };
}

function mapOngoingEducation(api: ClientDashboardOngoingEducation): OngoingTraining {
  const totalDuration = formatDuration(api.durationMinutes);
  const currentMinutes = Math.round((api.progressPercent / 100) * api.durationMinutes);
  const currentDuration = formatDuration(currentMinutes);
  return {
    id: api.educationId,
    title: api.educationName,
    instructor: api.instructorName,
    progressType: "duration",
    current: currentDuration,
    total: totalDuration,
    progressPercent: api.progressPercent,
  };
}

export interface MappedClientDashboard {
  stats: ClientDashboardStats;
  pendingExams: PendingExam[];
  ongoingTrainings: OngoingTraining[];
  upcomingEducations: ClientDashboardUpcomingEducation[];
}

/**
 * Fetches client dashboard from GET /api/client/dashboard and returns
 * mapped data for stats, pending exams, ongoing trainings, and upcoming educations.
 */
export async function getDashboard(): Promise<MappedClientDashboard> {
  const { data } = await apiClient.get<ClientDashboardApiResponse>(DASHBOARD_URL);
  const raw = data.data;

  if (import.meta.env.DEV) {
    console.log("[client-dashboard] API data:", raw);
  }

  return {
    stats: mapSummaryToStats(raw.summary),
    pendingExams: raw.pendingExams.map(mapPendingExam),
    ongoingTrainings: raw.ongoingEducations.map(mapOngoingEducation),
    upcomingEducations: raw.upcomingEducations ?? [],
  };
}

export const clientDashboardApi = {
  getDashboard,
};
